import { useContext } from "react";
import { useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

import { Button, CheckBox, Input } from "components/common/basic";
import { Banner } from "components/common/composite";
import { AccountFragment } from "graphql/fragments";
import {
  useAddAccountMemberMutation,
  useUpdateAccountMemberMutation,
} from "graphql/mutations";
import { TeamRoleEnum } from "graphql/types";
import { useErrorLogger } from "hooks";
import { AuthContext } from "providers/Authentication";
import { useTranslation } from "translations";
import { tw } from "utils/tw";

interface FormValues {
  firstName: string;
  lastName: string;
  systemAdmin: boolean;
}

interface Props {
  member: Partial<AccountFragment>;
  onSuccess: () => void;
  showAlreadyExistsNotice?: boolean;
}

export default ({
  member,
  onSuccess,
  showAlreadyExistsNotice = false,
}: Props): React.JSX.Element => {
  const { addAccountMember } = useAddAccountMemberMutation();
  const { updateAccountMember } = useUpdateAccountMemberMutation();

  const { session } = useContext(AuthContext);
  const { t } = useTranslation("common");
  const { reportErrors } = useErrorLogger();

  const isExisting = Boolean(member.id);

  const errorMessages = {
    firstName: t(
      "popup.teamMember.form.firstName.error.required",
      "A first name is required"
    ),
    lastName: t(
      "popup.teamMember.form.lastName.error.required",
      "A last name is required"
    ),
  };
  const validationSchema = Yup.object({
    firstName: Yup.string().trim().required(errorMessages.firstName),
    lastName: Yup.string().trim().required(errorMessages.lastName),
    systemAdmin: Yup.boolean().required(),
  });
  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<FormValues>({
    defaultValues: {
      firstName: member.primaryUser
        ? (member.primaryUser.firstName ?? "-")
        : "",
      lastName: member.primaryUser ? (member.primaryUser.lastName ?? "-") : "",
      systemAdmin: member.role === TeamRoleEnum.Admin,
    },
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });
  reportErrors(errors);
  const isOwner = member.role === TeamRoleEnum.Owner;

  const onSubmit = (values: FormValues) => {
    const role = values.systemAdmin ? TeamRoleEnum.Admin : TeamRoleEnum.Member;

    if (member.id)
      updateAccountMember(
        { memberId: member.id, attributes: { role } },
        onSuccess
      );
    else
      addAccountMember(
        {
          attributes: {
            role,
            email: member.email ?? "",
            firstName: values.firstName,
            lastName: values.lastName,
          },
        },
        onSuccess
      );
  };

  const formId = "addMemberForm";

  const hasAdminAccess = session?.role === TeamRoleEnum.Admin;
  const hasOwnerAccess = session?.role === TeamRoleEnum.Owner;

  return (
    <>
      <form
        className={tw("w-full", "space-y-9", "my-8")}
        id={formId}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div
          className={tw("space-y-4", "md:flex", "md:space-y-0", "md:space-x-4")}
        >
          <Input
            id="email"
            type="email"
            label={t("popup.teamMember.form.email.label", "Email")}
            value={member.email}
            fullWidth
            disabled
          />

          <Input
            id="firstName"
            {...register("firstName")}
            label={t("popup.teamMember.form.firstName.label", "First name")}
            placeholder={t(
              "popup.teamMember.form.firstName.placeholder",
              "First name"
            )}
            errorMessage={errors.firstName?.message}
            fullWidth
            disabled={isExisting}
          />

          <Input
            id="lastName"
            {...register("lastName")}
            label={t("popup.teamMember.form.lastName.label", "Last name")}
            placeholder={t(
              "popup.teamMember.form.lastName.placeholder",
              "Last name"
            )}
            errorMessage={errors.lastName?.message}
            fullWidth
            disabled={isExisting}
          />
        </div>

        {showAlreadyExistsNotice && (
          <Banner
            title={t(
              "popup.teamMember_edit.infoCard.heading",
              "Member already exists!"
            )}
            body={t(
              "popup.teamMember_edit.infoCard.body",
              "The member you are trying to add already exists in your members list. Edit their information below"
            )}
          />
        )}

        {isOwner ? (
          <Banner
            title={t(
              "popup.teamMember.form.owner.infoCard.heading",
              "Member's access"
            )}
            body={t(
              "popup.teamMember.form.owner.infoCard.body",
              "This member is the owner of this team account and they have all access rights."
            )}
          />
        ) : (
          (hasOwnerAccess || hasAdminAccess) && (
            <div
              className={tw(
                "p-6",
                "w-full",
                "space-y-6",
                "border",
                "border-deepBlue-100",
                "rounded-lg"
              )}
            >
              <div className={tw("w-full", "space-y-2")}>
                <h2 className={tw("text-lg", "font-bold")}>
                  {t(
                    "popup.teamMember.form.sytemAccess.heading",
                    "Grant team admin access"
                  )}
                </h2>

                <p>
                  {t(
                    "popup.teamMember.form.teamAdmin.helpText",
                    "A team admin can manage the team's members and settings."
                  )}
                </p>
              </div>

              <CheckBox
                id="systemAdmin"
                {...register("systemAdmin")}
                label={t(
                  "popup.teamMember.form.systemAdmin.label",
                  "Make this user a system admin"
                )}
              />
            </div>
          )
        )}
      </form>

      {!isOwner && (
        <Button
          id={
            isExisting
              ? "member_popup-update_member"
              : "member_popup-invite_member"
          }
          type="submit"
          form={formId}
          variant="primary"
          size="sm"
        >
          {isExisting
            ? t("popup.teamMember_update.form.submit", "Update member")
            : t("popup.teamMember_add.form.submit", "Invite member")}
        </Button>
      )}
    </>
  );
};
